/**
 * Reading, writing and resetting one setting or one field of a setting (W2-13 settings-keymap).
 *
 * A row edits a `SettingPath` (`section.key` or `ext.<feature>.<key>`), optionally one field inside an object value
 * (`audio.volumes` -> `master`, `checker.thresholds` -> `xMax`, `preview.player` -> `noteScale`). Writes go through
 * `SettingsService.set` / `setExt` (the service validates, persists and notifies), resets through
 * `SettingsService.reset`. Default values come from the service when it can tell them (`SettingsService.defaultOf`,
 * CR-W2-13-2); otherwise "is it the default?" is unknown and Reset stays on.
 */

import type { SettingPath, SettingsSection, SettingsService, UserSettings } from '../../app/settings/types';
import type { Json } from '../../core/util/types';

type Section = Exclude<SettingsSection, 'ext'>;

const SECTIONS: readonly Section[] = ['general', 'editing', 'audio', 'preview', 'keyboard', 'checker'];

function isRecord(v: unknown): v is Readonly<Record<string, unknown>> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Splits a path into its section and key (null for malformed paths). */
export function splitPath(path: string): { readonly section: SettingsSection; readonly key: string } | null {
  const dot = path.indexOf('.');
  if (dot <= 0 || dot === path.length - 1) return null;
  const section = path.slice(0, dot);
  if (section !== 'ext' && !(SECTIONS as readonly string[]).includes(section)) return null;
  return { section: section as SettingsSection, key: path.slice(dot + 1) };
}

/** Value of a path in a settings object (undefined for unknown paths and unset `ext` keys). */
export function valueAtPath(s: UserSettings, path: SettingPath): unknown {
  const p = splitPath(path);
  if (p === null) return undefined;
  if (p.section === 'ext') return s.ext[p.key];
  const section = s[p.section] as Readonly<Record<string, unknown>>;
  return section[p.key];
}

/** Value of a field inside an object value (`field` = [] for the value itself). */
export function fieldOf(value: unknown, field: readonly string[]): unknown {
  let cur = value;
  for (const f of field) {
    if (!isRecord(cur)) return undefined;
    cur = cur[f];
  }
  return cur;
}

/** Copy of an object value with one (possibly nested) field replaced. */
export function withField(value: unknown, field: readonly string[], v: unknown): unknown {
  if (field.length === 0) return v;
  const [head, ...rest] = field as [string, ...string[]];
  const base = isRecord(value) ? value : {};
  return { ...base, [head]: withField(base[head], rest, v) };
}

/** Structural equality of JSON-like values. */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (Array.isArray(a)) return Array.isArray(b) && a.length === b.length && a.every((x, i) => deepEqual(x, b[i]));
  if (isRecord(a) && isRecord(b)) {
    const ka = Object.keys(a).filter((k) => a[k] !== undefined);
    const kb = Object.keys(b).filter((k) => b[k] !== undefined);
    return ka.length === kb.length && ka.every((k) => Object.hasOwn(b, k) && deepEqual(a[k], b[k]));
  }
  return false;
}

/** Writes a whole setting value. */
export function writeSetting(settings: SettingsService, path: SettingPath, value: unknown): void {
  const p = splitPath(path);
  if (p === null) return;
  if (p.section === 'ext') {
    settings.setExt(p.key, value as Json);
    return;
  }
  // The service validates the value against the settings schema (enums checked, numbers clamped).
  const loose = settings as unknown as { set(section: Section, key: string, v: unknown): void };
  loose.set(p.section, p.key, value);
}

/** Writes one field of a setting (the rest of the object keeps its current values). */
export function writeField(settings: SettingsService, path: SettingPath, field: readonly string[], v: unknown): void {
  if (field.length === 0) {
    writeSetting(settings, path, v);
    return;
  }
  writeSetting(settings, path, withField(valueAtPath(settings.value, path), field, v));
}

/** Default value of a setting, or `unknown` when the service cannot tell. */
export function defaultOf(
  settings: SettingsService,
  path: SettingPath,
): { readonly known: boolean; readonly value: unknown } {
  if (settings.defaultOf === undefined) return { known: false, value: undefined };
  try {
    return { known: true, value: settings.defaultOf(path) };
  } catch {
    return { known: false, value: undefined };
  }
}

/** True / false when known; null when the service cannot tell the default. */
export function isDefaultValue(
  settings: SettingsService,
  path: SettingPath,
  field: readonly string[] = [],
): boolean | null {
  const d = defaultOf(settings, path);
  if (!d.known) return null;
  return deepEqual(fieldOf(valueAtPath(settings.value, path), field), fieldOf(d.value, field));
}

/**
 * Resets a setting or one of its fields. A field reset keeps the other fields: with a known default it writes
 * the default field; otherwise it resets the whole setting through the service and writes the other fields back.
 */
export function resetSetting(settings: SettingsService, path: SettingPath, field: readonly string[] = []): void {
  if (field.length === 0) {
    settings.reset(path);
    return;
  }
  const d = defaultOf(settings, path);
  if (d.known) {
    writeField(settings, path, field, fieldOf(d.value, field));
    return;
  }
  const before = valueAtPath(settings.value, path);
  settings.reset(path);
  const restored = fieldOf(valueAtPath(settings.value, path), field);
  writeSetting(settings, path, withField(before, field, restored));
}

/** Section of a setting path on the Settings page (ext preferences go to "Panels and tools"). */
export function pageSectionOfPath(path: string): Section | 'features' | null {
  const p = splitPath(path);
  if (p === null) return null;
  return p.section === 'ext' ? 'features' : p.section;
}
